/**
 * CONTRACT.md §2.15 — the shell-less pages: `/login`, and anything else that has
 * no sidebar to say where it is.
 *
 * Two things the contract is explicit about and this file carries.
 *
 * **There is no `.splash__title`.** §2.15: "app.css sets the two headings by
 * element … Write a bare `<h1>` / `<h2>`." So `title` is a prop and the element
 * is written here — a page that had to write the `<h1>` itself would be writing
 * markup, and a page that wrote an `<h2>` in the brand column would get nothing.
 *
 * **There is no `.brandmark`.** §2.15: these pages are "the **only** place §2.1's
 * shell classes may be hand-authored: reuse `.sidebar__mark` + `.sidebar__name`
 * for the brand lockup, so it is byte-identical to what `shell.js` emits
 * everywhere else." That lockup is built in, so there is one copy of it.
 *
 * `.splash__brand` is the second of the three dark surfaces the ink-colour
 * remap names, which is why `Muted`, `Prose` and `HeroCap` still read on it.
 */

import type { ReactNode } from 'react'

import type { Common } from './common.js'

/** The wordmark, from `apps/web/public/`. 2476×575, so height-only (§2.1). */
const LOGO = '/logo-paper.png'

export type SplashProps = Common & { readonly children: ReactNode }

/** The two-column page: ink on the left, paper on the right. One column below 1024px. */
export function Splash({ children, ...common }: SplashProps) {
  return (
    <div className="splash__wrap" {...common}>
      {children}
    </div>
  )
}

export type SplashBrandProps = Common & {
  /** The portal label beside the wordmark — "Client portal", "Studio portal". */
  readonly label: string
  readonly title: ReactNode
  /** The line under the title. */
  readonly lede?: ReactNode | undefined
  /** The foot of the column — support addresses, the portal's own URL. */
  readonly children?: ReactNode
}

export function SplashBrand({ label, title, lede, children, ...common }: SplashBrandProps) {
  return (
    <div className="splash__brand" {...common}>
      <span className="row">
        <img className="sidebar__mark" src={LOGO} alt="Far East Studios" />
        <span className="sidebar__name">{label}</span>
      </span>

      <div className="stack">
        <h1>{title}</h1>
        {lede === undefined ? null : <p className="small">{lede}</p>}
      </div>

      {children}
    </div>
  )
}

export type SplashFormProps = Common & { readonly children: ReactNode }

export function SplashForm({ children, ...common }: SplashFormProps) {
  return (
    <div className="splash__form" {...common}>
      {children}
    </div>
  )
}

export type SplashCardProps = Common & {
  readonly title: ReactNode
  /** The paragraph under the heading, at `.prose` rank. */
  readonly lede?: ReactNode | undefined
  readonly children: ReactNode
}

export function SplashCard({ title, lede, children, ...common }: SplashCardProps) {
  return (
    <div className="splash__card" {...common}>
      <div className="stack">
        <div className="stack stack--tight">
          <h2>{title}</h2>
          {lede === undefined ? null : <p className="prose">{lede}</p>}
        </div>
        {children}
      </div>
    </div>
  )
}
